import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from './useAuth';
import { LoadingState } from '@/components/states/LoadingState';
import { EmptyState } from '@/components/states/EmptyState';
import { AccountPicker } from './AccountPicker';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import { LogoMark } from '@/components/brand/Logo';

/**
 * Gate for protected routes (/panel/*, /scan).
 *
 * IMPORTANT: this component does NOT auto-call `sdk.login()`. Doing so
 * created an invisible redirect loop — if id.rsv.mx fails to round-trip
 * for any reason (Turnstile, cookie issue, allowlist) we'd just push the
 * user there over and over.
 *
 * Instead we render an explicit "Iniciar sesión" screen with a button.
 * The user clicks → SDK redirects → if the round-trip succeeds, the page
 * comes back authenticated and renders the panel. If it fails, the user
 * lands here again and can see the screen instead of being stuck in a
 * silent loop.
 *
 * Match's rsv-manager behavior: their onNoAuth calls `requestAuth()` (UI),
 * not `managerAuth.login()` (commented out for the same reason).
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const {
    isAuthenticated,
    isLoading,
    accountsLoading,
    accounts,
    selectedAccount,
    needsAccountPick,
    login,
    logout,
  } = useAuth();

  if (isLoading) {
    return <LoadingState label="Cargando…" />;
  }

  if (!isAuthenticated) {
    return <SignInRequired onLogin={login} />;
  }

  if (selectedAccount) {
    return <>{children}</>;
  }

  if (needsAccountPick) {
    return <AccountPicker />;
  }

  if (accountsLoading) {
    return <LoadingState label="Cargando cuentas…" />;
  }

  if (accounts.length === 0) {
    return (
      <Container size="sm">
        <EmptyState
          title="Sin acceso a clientes"
          description="Tu usuario no tiene asignado ningún cliente con la boletera habilitada. Pídele al super-admin que active el feature `tickets` para tu organización."
          action={
            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
              <Link to="/">
                <Button variant="secondary">Inicio</Button>
              </Link>
              <Button variant="ghost" onClick={() => void logout()}>
                Cerrar sesión
              </Button>
            </div>
          }
        />
      </Container>
    );
  }

  return <LoadingState label="Cargando cuentas…" />;
}

function SignInRequired({ onLogin }: { onLogin: () => void }) {
  return (
    <Container size="sm">
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          gap: 'var(--space-4)',
          minHeight: '70dvh',
          padding: 'var(--space-7) 0',
        }}
      >
        <LogoMark size={48} />
        <h1
          style={{
            margin: 0,
            fontSize: 'clamp(1.5rem, 5vw, 1.875rem)',
            fontWeight: 700,
            letterSpacing: '-0.02em',
          }}
        >
          Inicia sesión
        </h1>
        <p
          style={{
            margin: 0,
            color: 'var(--color-text-muted)',
            fontSize: '0.9375rem',
            maxWidth: '30rem',
          }}
        >
          Para acceder al panel necesitas entrar con tu cuenta de RSV ID.
        </p>
        <Button size="lg" onClick={onLogin}>
          Continuar con RSV ID
        </Button>
        <p
          style={{
            margin: 0,
            color: 'var(--color-text-subtle)',
            fontSize: '0.8125rem',
            maxWidth: '32rem',
          }}
        >
          Si vienes de iniciar sesión y sigues viendo esta pantalla, vuelve a hacer click — id.rsv.mx puede pedir un challenge anti-bot que tienes que completar manualmente.
        </p>
      </div>
    </Container>
  );
}
